/**
 * TASK QUERIES - READ OPERATIONS FOR TASKS
 *
 * This file contains React Query hooks for fetching task data:
 * - Individual task fetching
 * - Tasks by topic fetching
 * - Available tags computation
 *
 * All hooks support offline mode with IndexedDB fallback.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '../../../lib/supabase';
import { logger } from '../../../utils/logger';
import { taskKeys } from '../taskKeys';
import { TaskData, formatTask } from '../taskTypes';
import { db } from '../../../lib/offline-db';
import { getConnectionState } from '../../../contexts/ConnectionStateContext';
import { useQueryErrorHandler } from '../../useQueryErrorHandler';
import {
  enrichTasksWithAssigneeInfo,
  enrichTaskWithAssigneeInfo,
} from '../../../utils/assigneeHelper';
import { sortTasks } from './taskHelpers';

/**
 * Hook for fetching all tasks for a specific topic
 * Supports offline mode by using IndexedDB when offline
 */
export function useTasks(topicId: string | null) {
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useQuery({
    queryKey: topicId ? taskKeys.byTopic(topicId) : taskKeys.lists(),
    queryFn: async () => {
      if (!topicId) {
        throw new Error('Topic ID is required');
      }

      const fetchId = Date.now().toString().slice(-4);
      logger.fetch(`Starting tasks fetch #${fetchId} for topic ${topicId} (online: ${isOnline})`);
      const startTime = performance.now();

      try {
        // If offline, get data from IndexedDB
        if (!isOnline) {
          logger.fetch(`Offline mode: fetching tasks from IndexedDB #${fetchId}`);

          try {
            // Get tasks from IndexedDB
            const tasks = await db.tasks
              .where('topic_id')
              .equals(topicId)
              .and(task => !task.isDeleted) // Filter out deleted tasks
              .toArray();

            // Sort tasks consistently
            const sortedTasks = sortTasks(tasks as TaskData[]);

            logger.fetch(`Offline mode: loaded ${tasks.length} tasks from IndexedDB`);

            // Enrich tasks with assignee info
            const enrichedTasks = enrichTasksWithAssigneeInfo(sortedTasks);

            // Format tasks using the helper function
            return enrichedTasks.map(task => formatTask(task as TaskData));
          } catch (offlineError) {
            logger.error(`Error fetching offline tasks #${fetchId}:`, offlineError);
            throw new Error('Failed to load tasks from offline storage');
          }
        }

        // Online flow - fetch from server
        const { data, error } = await supabase
          .from('tasks')
          .select('*')
          .eq('topic_id', topicId)
          .order('position');

        if (error) throw error;

        // Store in IndexedDB for offline access
        try {
          if (data && data.length > 0) {
            await db.tasks.bulkPut(data);
            logger.fetch(`Stored ${data.length} tasks in IndexedDB`);
          }
        } catch (storageError) {
          logger.error(`Error storing tasks in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        // Sort tasks consistently
        const sortedTasks = sortTasks(data || []);

        // Enrich tasks with assignee info
        const enrichedTasks = enrichTasksWithAssigneeInfo(sortedTasks);

        // Format tasks using the helper function
        const formattedTasks = enrichedTasks.map(task => formatTask(task as TaskData));

        // Log performance metrics
        const endTime = performance.now();
        const duration = endTime - startTime;
        logger.fetch(`Tasks fetch #${fetchId} completed in ${duration.toFixed(2)}ms`);

        return formattedTasks;
      } catch (error) {
        // Use the error handler to handle and log the error
        const appError = handleError(error, {
          action: 'silent',
          context: {
            fetchId,
            operation: 'fetchTasks',
            topicId,
            isOnline,
          },
        });

        throw appError;
      }
    },
    enabled: !!topicId,
  });
}

/**
 * Hook for fetching a specific task by ID
 * Supports offline mode by using IndexedDB when offline
 */
export function useTask(taskId: string | null) {
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useQuery({
    queryKey: taskId ? taskKeys.detail(taskId) : taskKeys.details(),
    queryFn: async () => {
      if (!taskId) {
        throw new Error('Task ID is required');
      }

      logger.fetch(`Fetching task ${taskId} (online: ${isOnline})`);

      try {
        // If offline, get data from IndexedDB
        if (!isOnline) {
          logger.fetch(`Offline mode: fetching task ${taskId} from IndexedDB`);

          try {
            // Get task from IndexedDB
            const task = await db.tasks.get(taskId);

            if (!task || task.isDeleted) {
              throw new Error(`Task ${taskId} not found in offline storage`);
            }

            // Enrich task with assignee info
            const enrichedTask = enrichTaskWithAssigneeInfo(task as TaskData);

            // Format the task
            return formatTask(enrichedTask as TaskData);
          } catch (offlineError) {
            logger.error(`Error fetching offline task ${taskId}:`, offlineError);
            throw new Error('Failed to load task from offline storage');
          }
        }

        // Online flow - fetch from server
        const { data, error } = await supabase.from('tasks').select('*').eq('id', taskId).single();

        if (error) throw error;

        // Store in IndexedDB for offline access
        try {
          await db.tasks.put(data);
        } catch (storageError) {
          logger.error(`Error storing task in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        // Enrich task with assignee info
        const enrichedTask = enrichTaskWithAssigneeInfo(data as TaskData);

        // Format and return the task
        return formatTask(enrichedTask as TaskData);
      } catch (error) {
        const appError = handleError(error, {
          action: 'silent',
          context: { operation: 'fetchTask', taskId, isOnline },
        });
        throw appError;
      }
    },
    enabled: !!taskId,
  });
}

/**
 * Hook for getting all available tags for a topic
 * Returns a list of unique tags used in tasks
 */
export function useAvailableTags(topicId: string | null) {
  const { data: tasks } = useTasks(topicId);

  // Calculate unique tags from tasks
  const uniqueTags = useMemo(() => {
    if (!tasks) return [];

    // Extract all tags from all tasks
    const allTags: string[] = [];
    tasks.forEach(task => {
      if (task.tags && task.tags.length > 0) {
        allTags.push(...task.tags);
      }
    });

    // Return unique tags (no duplicates)
    return [...new Set(allTags)];
  }, [tasks]);

  return uniqueTags;
}
